import { globalRegistry, uuid } from "zod";
/** @import { output } from "zod" */

/**
 * Zod schema and type for a {@link UUIDv4} (Universally Unique Identifier).
 *
 * This schema validates strings conforming to
 * [RFC 4122](https://www.rfc-editor.org/rfc/rfc4122), specifically UUID version
 * 4 which are randomly generated.
 *
 * @example "2b6506e8-72fd-49d7-91a3-04839a3bbc99"
 *
 * @example
 * ```javascript
 * // Parsing a UUID
 * const parsed = UUIDv4.parse("f47ac10b-58cc-4372-a567-0e02b2c3d479");
 * ```
 *
 * @example
 * ```javascript
 * // Generating a UUID v4
 * const id = UUIDv4.generate();
 * ```
 *
 * @see {@link https://datatracker.ietf.org/doc/html/rfc4122}
 * @see {@link https://www.rfc-editor.org/rfc/rfc4122}
 */
export const UUIDv4 = Object.assign(uuid({ version: "v4" }), {
  /**
   * Generate a UUID v4
   *
   * @note This depends on the `globalThis.crypto` module
   * @returns {UUIDv4}
   */
  generate() {
    return UUIDv4.parse(crypto.randomUUID());
  },
});
/** @typedef {output<typeof UUIDv4>} UUIDv4 */

globalRegistry.add(UUIDv4, {
  description: "A universally unique identifier (UUID) version 4.",
  examples: ["2b6506e8-72fd-49d7-91a3-04839a3bbc99", "f47ac10b-58cc-4372-a567-0e02b2c3d479"],
  link: "https://datatracker.ietf.org/doc/html/rfc4122",
});
